import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const STEP_LABELS = {
  encode: "Encodage vidéo",
  mux: "Assemblage MP4",
  sps: "SPS/PPS introuvables",
  avc_probe: "Encodeur H.264 inexploitable",
  unsupported: "Navigateur non supporté",
  clips_not_ready: "Vidéos pas prêtes",
  clip_undecodable: "Clip de banque remplacé",
  server_audio: "Son (serveur)",
  empty: "Fichier vide",
  prepare: "Préparation",
};

const Pill = ({ label, value, on, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 text-xs border transition-colors ${on ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-foreground"}`}>
    {label} <span className="font-osd ml-1">{value}</span>
  </button>
);

export const ExportErrorsAdmin = () => {
  const [days, setDays] = useState(7);
  const [browser, setBrowser] = useState("");
  const [step, setStep] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/export-errors?days=${days}&browser=${encodeURIComponent(browser)}&step=${encodeURIComponent(step)}&limit=200`);
      setData(data);
    } catch {
      setData(null);
    }
  }, [days, browser, step]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="bg-card border border-border p-6 sm:p-8 mb-8" data-testid="export-errors-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-lg">Erreurs d'export</h2>
          <p className="text-xs text-muted-foreground mt-1">Échecs remontés par le studio (navigateur, étape, clip) — collection <code>export_errors</code>.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="export-errors-days-select"
            className="bg-secondary border border-border text-xs px-2 py-1.5">
            <option value={1}>24 h</option>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
          </select>
          <button type="button" onClick={load} data-testid="export-errors-refresh"
            className="px-3 py-1.5 text-xs border border-border text-muted-foreground hover:border-foreground">Actualiser</button>
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2" data-testid="export-errors-browsers">
            <Pill label="Tous" value={Object.values(data.by_browser || {}).reduce((a, b) => a + b, 0)} on={!browser} onClick={() => setBrowser("")} testid="export-errors-browser-all" />
            {Object.entries(data.by_browser || {}).map(([k, v]) => (
              <Pill key={k} label={k} value={v} on={browser === k} onClick={() => setBrowser(browser === k ? "" : k)} testid={`export-errors-browser-${k.replace(/\s+/g, "-").toLowerCase()}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="export-errors-steps">
            {Object.entries(data.by_step || {}).map(([k, v]) => (
              <Pill key={k} label={STEP_LABELS[k] || k} value={v} on={step === k} onClick={() => setStep(step === k ? "" : k)} testid={`export-errors-step-${k}`} />
            ))}
          </div>

          {data.total === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="export-errors-empty">Aucune erreur d'export sur la période.</p>
          ) : (
            <div className="overflow-x-auto">
              <p className="font-osd text-xs text-muted-foreground mb-2" data-testid="export-errors-total">{data.total} erreur{data.total > 1 ? "s" : ""}</p>
              <table className="w-full text-xs" data-testid="export-errors-table">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="py-1 pr-3">Quand</th><th className="py-1 pr-3">Navigateur</th><th className="py-1 pr-3">Étape</th>
                    <th className="py-1 pr-3">Mode</th><th className="py-1 pr-3">Utilisateur</th><th className="py-1 pr-3">Message</th><th className="py-1">Clip / codec</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-border align-top cursor-pointer hover:bg-secondary/40" onClick={() => setOpen(open === i ? null : i)} data-testid={`export-error-row-${i}`}>
                      <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleString("fr-FR") : "—"}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{it.browser || "?"}<span className="text-muted-foreground"> · {it.os || "?"}</span><div className="text-muted-foreground">{it.build}{it.avc ? ` · ${it.avc}` : ""}</div></td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{STEP_LABELS[it.step] || it.step}</td>
                      <td className="py-1.5 pr-3">{it.mode || "—"}</td>
                      <td className="py-1.5 pr-3 truncate max-w-[180px]">{it.email || it.user_id || "—"}</td>
                      <td className="py-1.5 pr-3 max-w-[360px]">
                        <div className={open === i ? "" : "truncate"}>{it.message || "—"}</div>
                        {open === i && it.stack ? <pre className="mt-2 text-[10px] whitespace-pre-wrap text-muted-foreground max-h-48 overflow-auto" data-testid={`export-error-stack-${i}`}>{it.stack}</pre> : null}
                        {open === i && it.project_id ? <div className="mt-1 text-muted-foreground">projet {it.project_id}</div> : null}
                      </td>
                      <td className="py-1.5 text-muted-foreground">{it.clip_id ? <span className="font-osd">{it.clip_id.slice(0, 10)}</span> : ""}{it.replaced_by ? ` → ${it.replaced_by.slice(0, 10)}` : ""}{it.codec ? <div>{it.codec}</div> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
